import { Component, ViewChild, ViewEncapsulation, AfterViewInit, OnInit } from '@angular/core';
import { BaseModalComponent } from '../../../../models/base-modal-component';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { RestService } from '../../../../services/rest/rest.service';
import { CommonService } from '../../../../services/common/common.service';
import { GenericTreeComponent } from '../../../generic-tree/generic-tree.component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { Endpoint, OperationType, ReviewSystem } from '../../../../models/enums';
import { EditBucketModalComponent, EditMode } from '../../edit-bucket/edit-bucket-modal.component';
import { ContextMenuOption } from '../../../../models/context-menu-option';

@Component({
    standalone: false,
    selector: 'select-document-folder-modal',
    templateUrl: './select-document-folder-modal.component.html',
    styleUrls: ['./select-document-folder-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
    })
export class SelectDocumentFolderModalComponent extends BaseModalComponent implements AfterViewInit, OnInit {
    // modal info
    modalTitle = 'Select Document Folder';
    modalAutomationId = 'target-picker';
    secondaryButtonLabel = 'Cancel';
    primaryButtonLabel = 'OK';
    modalClass = 'select-document-folder-modal';
    isBusy = false;
    isBusyMessage = '';
    projectNameLabel = 'Project';
    reviewProjectInfo = null;

    // tree contextmenuoptions
    contextMenuOptions = [
        new ContextMenuOption({
            name: 'New Folder ...', iconClass: 'fa-plus', iconColor: 'color-green-04',
            click: (folder: GenericTreeNode) => this.createEditNewFolder(folder, OperationType.new)
        }), new ContextMenuOption({
            name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01',
            click: (folder: GenericTreeNode) => this.createEditNewFolder(folder, OperationType.edit)
        })
    ];

    // tree info
    @ViewChild(GenericTreeComponent) tree: GenericTreeComponent;
    rootFolder: GenericTreeNode;

    constructor(
        private modalService: BsModalService,
        private restService: RestService,
        private commonService: CommonService,
        private modalRef: BsModalRef,
    ) {
        super(modalService, modalRef);
    }

    ngOnInit(): void {
        if (this.modalData?.reviewProjectInfo?.name) {
            this.projectNameLabel = this.modalData?.reviewProjectInfo.name;
        }
    }

    ngAfterViewInit() {
        this.isBusy = true;
        this.isBusyMessage = 'Loading review folder structure ...';
        this.restService.post(Endpoint.orgManager_listReviewSystemFolders, {
            projectHandle: this.commonService.currentProject.handle,
            reviewProjectId: this.modalData?.reviewProjectInfo.id,
            orgName: this.commonService.currentUser?.originalCustomerName === 'super_system_customer' ? 'super_system_customer' : this.commonService?.currentUser?.customerName,
            reviewSystem: this.modalData?.reviewProjectInfo.reviewSystem
        }).then(response => {
            if (response?.folders || response?.reefReviewFolders) {
                const topLevelChildNodes: Array<GenericTreeNode> = [];
                const responseFolders = response.folders.length > 0 ? response.folders :  response.reefReviewFolders;
                responseFolders.forEach(folder => {
                    topLevelChildNodes.push(new GenericTreeNode({
                        name: folder.name,
                        icon: 'fa-folder',
                        iconExpanded: folder.subfolders?.length > 0 || folder.hasChild ? 'fa-folder-open' : 'fa-folder',
                        iconColor: folder.name === 'Unfoldered' ? 'color-black-01' : 'color-tan-07',
                        isExpandable: folder.subfolders?.length > 0 || folder.hasChild,
                        isExpandedOnSelect: folder.subfolders?.length > 0 || folder.hasChild,
                        hasExpanderPadding: true,
                        data: folder,
                        customNumber: folder.documentCount,
                        contextMenuOptions: folder.name === 'Unfoldered' ? null : this.contextMenuOptions
                    }));
                });

                // Adding 'Unfoldered' document to the top of the list
                topLevelChildNodes.sort((folderA, folderB) => (folderA.name === 'Unfoldered' ? -1 : folderB.name === 'Unfoldered' ? 1 : 0));

                this.rootFolder = new GenericTreeNode({
                    name: 'All Document Folders', isExpandable: true, isExpanded: true, isExpandedOnSelect: true,
                    automationId: 'all-document-folders-autoId',
                    hasExpanderPadding: true, data: { name: 'All Document Folders' },
                    icon: 'fa-folder',
                    iconExpanded: 'fa-folder-open',
                    iconColor: 'color-blue-01',
                    contextMenuOptions: null,
                    children: topLevelChildNodes, customNumber: response.totalCount
                });
                this.tree.setData([this.rootFolder]);
                this.isBusy = false;
            }
        });
    }

    createEditNewFolder(folder?: GenericTreeNode, operationType?: string) {
        const parentFolder = folder;
        const selectedFolder = this.tree.selectedNode;
        this.commonService.openModal(EditBucketModalComponent, {
            modalData: {
                mode: operationType === OperationType.edit ? EditMode.EDITREVIEWFOLDER : folder ? EditMode.NEWREVIEWSUBFOLDER : EditMode.NEWREVIEWFOLDER,
                isDescriptionHidden: true,
                isColorListTemplateHidden: true,
                parentFolderId: folder ? folder.data?.id : null,
                reviewProjectId: this.modalData.reviewProjectInfo.id
            },
            class: 'horizontal-px-430 vertical-px-60'
        }).then((value) => {
            if (value.modalData?.folder || value.modalData?.reefReviewFolder) {
                const folder = value.modalData?.folder || value.modalData?.reefReviewFolder;
                if (folder) {
                    const subFolder = new GenericTreeNode({
                        name: folder.name,
                        icon: 'fa-folder',
                        iconExpanded: folder.subfolders?.length > 0 || folder.hasChild ? 'fa-folder-open' : 'fa-folder',
                        iconColor: 'color-tan-07',
                        isExpandable: folder.subfolders?.length > 0 || folder.hasChild,
                        isExpandedOnSelect: folder.subfolders?.length > 0 || folder.hasChild,
                        hasExpanderPadding: true,
                        data: folder,
                        customNumber: folder.documentCount,
                        contextMenuOptions: this.contextMenuOptions
                    });
                    if (parentFolder || selectedFolder) {
                        const folder = parentFolder ?? selectedFolder;
                        // add already existing subfolders/children to the folder and push the newly added subFolder to the nodes
                        if (folder?.children?.length < folder?.data?.subfolders?.length) {
                            const subfolders = this.createSubfolderNodes(folder);
                            folder.children = subfolders;
                        }
                        folder?.children?.push(subFolder);
                        if (!folder.isExpanded) {
                            folder.isExpandable = true;
                            folder.isExpanded = true;
                            folder.isExpandedOnSelect = true;
                            this.tree.treeControl.expand(folder);
                        }
                        this.tree.treeDataUpdated();
                        // select the newly created subfolder
                        const selNode = this.tree.getNodeByField('id', subFolder.data?.id, folder);
                        if (selNode) {
                            this.tree.selectNode(selNode);
                        }
                    }
                }
            } else if (value.modalData?.reefReviewShortItemFolder) {
                const reviewObject = value.modalData?.reefReviewShortItemFolder;
                const node = this.tree.getNodeBy(n => n.data.id === reviewObject.id);
                if (node) {
                    node.name = reviewObject.name;
                    node.data.name = reviewObject.name;
                    node.data.path = reviewObject.path;
                    this.tree.treeDataUpdated();
                    this.tree.selectNode(node);
                }
            }
        });
    }

    createSubfolderNodes(folder: any): Array<GenericTreeNode> {
        const subFolderNodes = new Array<GenericTreeNode>();
        const subfolders = folder?.data?.subfolders?.length > 0 ? folder.data.subfolders : folder?.subfolders?.length > 0 ? folder.subfolders : [];
        if (subfolders.length > 0) {
            subfolders.forEach(folder => {
                subFolderNodes.push(new GenericTreeNode({
                    name: folder.name,
                    icon: 'fa-folder',
                    iconExpanded: folder.subfolders?.length > 0 ? 'fa-folder-open' : 'fa-folder',
                    iconColor: folder.name === 'Unfoldered' ? 'color-black-01' : 'color-tan-07',
                    isExpandable: folder.subfolders?.length > 0,
                    isExpandedOnSelect: folder.subfolders?.length > 0,
                    hasExpanderPadding: true,
                    data: folder,
                    customNumber: folder.documentCount,
                    contextMenuOptions: folder.name === 'Unfoldered' ? null : this.contextMenuOptions
                }));
            });
        }
        return subFolderNodes;
    }

    isPrimaryButtonEnabled() {
        return Boolean(this.tree && this.tree.selectedNode && this.tree.selectedNode.name !== 'All Document Folders');
    }
    
    isNewFolderButtonEnabled()
    {
        return Boolean(this.tree && this.tree.selectedNode);
    }

    ok() {
        if (this.tree.selectedNode) {
            if (this.tree.selectedNode.parent) {
                this.closeModal(true, { parentFolderName: this.tree.selectedNode.parent.name, subfolder: this.tree.selectedNode });
            } else {
                this.closeModal(true, this.tree.selectedNode);
            }
        }
    }

    loadNodeDataCallback = (node: GenericTreeNode) => {
        if ((node?.data?.hasChild && !node?.data?.subfolders) && this.commonService.currentProject?.reviewSystem === ReviewSystem.RELATIVITY) {
            node.isLoading = true;
            this.restService.post(Endpoint.orgManager_listReviewSystemFolders, {
                projectHandle: this.commonService.currentProject.handle,
                reviewProjectId: this.modalData.reviewProjectInfo.id,
                orgName: this.commonService.currentUser?.originalCustomerName === 'super_system_customer' ? 'super_system_customer' : this.commonService?.currentUser?.customerName,
                reviewSystem: this.modalData.reviewProjectInfo.reviewSystem,
                parentFolderId: node.data.id
            }).then(response => {
                if (response?.folders) {
                    node.data.subfolders = response.folders;
                    this.setSubfolders(node);
                }
                node.isLoading = false;
            }, () => {
                node.isLoading = false;
            });
        } else {
            this.setSubfolders(node);
        }
    }

    setSubfolders(node: GenericTreeNode): void {
        const subfolders = node.data.subfolders;
        const nodeChildren: Array<GenericTreeNode> = [];
        if (subfolders?.length > 0) {
            subfolders.forEach(folder => {
                nodeChildren.push(new GenericTreeNode({
                    name: folder.name,
                    icon: 'fa-folder',
                    iconExpanded: folder.subfolders?.length > 0 || folder.hasChild ? 'fa-folder-open' : 'fa-folder',
                    iconColor: 'color-tan-07',
                    isExpandable: folder.subfolders?.length > 0 || folder.hasChild,
                    isExpandedOnSelect: folder.subfolders?.length > 0 || folder.hasChild,
                    hasExpanderPadding: true,
                    data: folder,
                    customNumber: folder.documentCount,
                    contextMenuOptions: this.contextMenuOptions
                }));
            });
        }
        this.tree.setNodeData(node, nodeChildren);
    }
}
